import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation } from '@apollo/client/react';
import { useParams } from 'react-router-dom';
import Breadcrumb from '../components/Breadcrumb';
import QueryError from '../components/QueryError';
import EmptyState from '../components/EmptyState';
import M3LinearProgress from '../components/M3Progress';
import FigurePreview from '../components/shared/FigurePreview';
import GoodRelationsCard from '../components/GoodRelationsCard';
import { GRAVE_RELATIONS_QUERY } from '../graphql/queries';
import { SAVE_RELATED } from '../graphql/mutations';
import '../styles/grave-related.css';

// Full-drawing types that can be linked to a grave's goods (legacy
// Relations candidates).
const RELATION_TYPES = ['Ceramic', 'StoneTool', 'Artefact', 'ShaftAxe'];

const goodLinkedIds = (good) => [
  ...(good.artefacts || []),
  ...(good.ceramics || []),
  ...(good.lithics || [])
].map((f) => f.id);

export default function GraveRelatedPage() {
  const { id } = useParams();
  const { data, loading, error } = useQuery(GRAVE_RELATIONS_QUERY, { variables: { id } });
  const [saveRelated, { loading: saving }] = useMutation(SAVE_RELATED, {
    refetchQueries: [{ query: GRAVE_RELATIONS_QUERY, variables: { id } }],
    awaitRefetchQueries: true
  });

  const grave = data?.grave;
  const goods = grave?.goods || [];
  const pageFigures = grave?.page?.figures || [];

  const initialRelations = useMemo(
    () => Object.fromEntries(goods.map((g) => [g.id, goodLinkedIds(g)])),
    [goods]
  );
  const [relations, setRelations] = useState({});
  const [selectedGoodId, setSelectedGoodId] = useState(null);

  useEffect(() => {
    setRelations(initialRelations);
    setSelectedGoodId(null);
  }, [initialRelations]);

  if (error)
    return (
      <div className="page-content">
        <QueryError error={error} onRetry={() => window.location.reload()} />
      </div>
    );
  if (loading || !grave)
    return (
      <div className="page-content">
        <M3LinearProgress indeterminate />
      </div>
    );

  const figuresById = new Map(
    [...pageFigures, ...goods.flatMap((g) => [...(g.artefacts || []), ...(g.ceramics || []), ...(g.lithics || [])])].map(
      (f) => [f.id, f]
    )
  );
  const candidates = pageFigures.filter((f) => RELATION_TYPES.includes(f.type));
  const dirty = JSON.stringify(relations) !== JSON.stringify(initialRelations);

  const toggle = (goodId, figureId) =>
    setRelations((prev) => {
      const current = prev[goodId] || [];
      const next = current.includes(figureId)
        ? current.filter((x) => x !== figureId)
        : [...current, figureId];
      return { ...prev, [goodId]: next };
    });

  const handleSave = () =>
    saveRelated({ variables: { input: { graveId: grave.id, relations } } }).catch((err) =>
      alert(err.message)
    );

  const selectedIds = [...(relations[selectedGoodId] || []), selectedGoodId].filter(Boolean);

  return (
    <div className="page-content detail-page">
      <Breadcrumb
        items={[
          { label: 'Graves', link: '/graves' },
          { label: grave.identifier || `Grave #${grave.id}`, link: `/graves/${grave.id}` },
          { label: 'Related Artefacts' }
        ]}
      />
      <div className="detail-header">
        <h1>Related Artefacts — {grave.identifier || `Grave #${grave.id}`}</h1>
        <button className="btn btn-primary" onClick={handleSave} disabled={!dirty || saving}>
          {saving ? 'Saving…' : 'Save Relations'}
        </button>
      </div>
      <p className="grave-related-hint">
        Select a good below, then click artefacts on the drawing or in the lists to link them.
      </p>
      <div className="grave-related-layout">
        <div className="grave-related-preview">
          {grave.page?.image ? (
            <FigurePreview
              figures={pageFigures}
              image={{ ...grave.page.image, src: grave.page.image.url }}
              onFigureClick={(figure) => {
                if (selectedGoodId && RELATION_TYPES.includes(figure.type))
                  toggle(selectedGoodId, figure.id);
              }}
              highlightIds={selectedIds}
            />
          ) : (
            <p className="grave-related-none">No page image found.</p>
          )}
        </div>
        <div className="grave-related-goods">
          {goods.length === 0 && <EmptyState message="This grave has no goods yet." />}
          {goods.map((good) => (
            <GoodRelationsCard
              key={good.id}
              good={good}
              selected={selectedGoodId === good.id}
              onSelect={() => setSelectedGoodId(good.id)}
              linkedIds={relations[good.id] || []}
              candidates={candidates.filter((f) => !(relations[good.id] || []).includes(f.id))}
              figuresById={figuresById}
              onToggle={toggle}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
